import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { format, parseISO, addDays, isWeekend, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Clock, Coins, User, Infinity } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookTrainer() {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const { user, token, dbUser, refreshUser } = useAuth();

  const [trainer, setTrainer] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  // Check if user has unlimited access
  const hasUnlimited = dbUser?.unlimited_until && new Date(dbUser.unlimited_until) > new Date();
  const userCredits = dbUser?.credits || 0;
  const creditsNeeded = 1.0;
  const hasEnoughCredits = hasUnlimited || userCredits >= creditsNeeded;





  const fetchTrainer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trainers/${trainerId}`);
      setTrainer(response.data);

      // Set initial date to today (we'll fetch slots and the user can navigate)
      let nextDate = new Date();
      
      setSelectedDate(nextDate);
    } catch (error) {
      console.error('Error fetching trainer:', error);
      toast.error('Erreur lors du chargement du coach');
    } finally {
      setLoading(false);
    }
  }, [trainerId]);
    useEffect(() => {
    fetchTrainer();
  }, [fetchTrainer]);
  const fetchSlots = useCallback(async (date) => {
    try {
      setSlots([]); // Clear previous slots
      const response = await axios.get(`${API}/slots/trainer/${trainerId}?date=${date}`);
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  }, [trainerId]);
  useEffect(() => {
    if (selectedDate && trainer) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      fetchSlots(dateStr);
    }
  }, [selectedDate, trainer, fetchSlots]);
  const handleBooking = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/login');
      return;
    }

    if (!selectedSlot) {
      toast.error('Veuillez sélectionner un créneau');
      return;
    }

    setBooking(true);
    try {
      await axios.post(
        `${API}/bookings`,
        {
          slot_id: selectedSlot.id,
          trainer_id: trainerId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Réservation confirmée ! 1 crédit a été utilisé.');

      // Refresh user data to update credits
      if (refreshUser) {
        await refreshUser();
      }

      navigate('/bookings');
    } catch (error) {
      console.error('Booking error:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la réservation';
      if (errorMessage.includes('Crédits insuffisants')) {
        toast.error('Crédits insuffisants. Achetez des crédits pour réserver.');
        navigate('/credits');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setBooking(false);
    }
  };

 const disabledDays = useCallback((date) => {
  return date < new Date();
}, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4">
            <Skeleton className="h-8 w-32 mb-8" />
            <div className="grid md:grid-cols-2 gap-8">
              <Skeleton className="h-96" />
              <Skeleton className="h-96" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-muted-foreground">Coach non trouvé</p>
          </div>
        </main>
      </div>
    );
  }

  // Session is 1 credit for 1h

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/trainers')}
            className="mb-6 group"
            data-testid="back-to-trainers"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Retour aux coachs
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Trainer Info */}
            <Card className="lg:col-span-1 h-fit" data-testid="trainer-info-card">
              <CardContent className="p-0">
                <div className="aspect-square overflow-hidden rounded-t-lg">
                  <img
                    src={trainer.photo_url}
                    alt={trainer.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.pexels.com/photos/1618042/pexels-photo-1618042.jpeg';
                    }}
                  />
                </div>
                <div className="p-6">
                  <h1 className="font-heading font-bold text-2xl mb-1">{trainer.name}</h1>
                  <p className="text-muted-foreground mb-4">{trainer.specialty}</p>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4" />
                      </div>
                      <span>Séance de 1h</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                        <Coins className="w-4 h-4" />
                      </div>
                      <span>1 crédit / séance</span>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-[#D4FF00]/10 rounded-lg border border-[#D4FF00]/30">
                    <p className="text-sm text-muted-foreground">Coût de la séance</p>
                    <p className="font-heading font-bold text-2xl">1 crédit</p>
                  </div>

                  {/* User credits display */}
                  {user && dbUser && (
                    <div className="mt-4 p-4 bg-secondary rounded-lg">
                      <p className="text-sm text-muted-foreground">Vos crédits</p>
                      {hasUnlimited ? (
                        <div className="flex items-center gap-2">
                          <Infinity className="w-5 h-5 text-[#D4FF00]" />
                          <span className="font-bold">Illimité</span>
                        </div>
                      ) : (
                        <p className="font-bold">{userCredits.toFixed(1)} heures</p>
                      )}
                      {!hasEnoughCredits && (
                        <Link to="/credits">
                          <Button size="sm" className="mt-2 bg-[#D4FF00] text-black hover:bg-[#bfe600]">
                            Acheter des crédits
                          </Button>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Booking Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Calendar */}
              <Card data-testid="booking-calendar-card">
                <CardHeader>
                  <CardTitle className="font-heading">Choisir une date</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    locale={fr}
                    className="rounded-md border w-fit"
                    data-testid="booking-calendar"
                  />
                  <p className="text-sm text-muted-foreground mt-4">
                    Disponible du lundi au dimanche
                  </p>
                </CardContent>
              </Card>

              {/* Time Slots */}
              <Card data-testid="time-slots-card">
                <CardHeader>
                  <CardTitle className="font-heading">
                    Créneaux disponibles
                    {selectedDate && (
                      <span className="text-muted-foreground font-normal text-base ml-2">
                        {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {slots.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      Aucun créneau disponible pour cette date
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {slots.map((slot) => (
                        <Button
                          key={slot.id}
                          variant={selectedSlot?.id === slot.id ? "default" : "outline"}
                          className={`h-auto py-3 ${selectedSlot?.id === slot.id
                              ? 'bg-[#D4FF00] text-black hover:bg-[#bfe600]'
                              : ''
                            }`}
                          onClick={() => setSelectedSlot(slot)}
                          data-testid={`slot-${slot.id}`}
                        >
                          <div className="text-center">
                            <p className="font-bold">{slot.start_time}</p>
                            <p className="text-xs opacity-70">- {slot.end_time}</p>
                          </div>
                        </Button>
                      ))}
                    </div>
                  )}

                  {selectedSlot && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="font-medium">Récapitulatif</p>
                          <p className="text-sm text-muted-foreground">
                            {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })} • {selectedSlot.start_time} - {selectedSlot.end_time}
                          </p>
                        </div>
                        <Badge className="bg-[#D4FF00] text-black text-lg font-bold px-4 py-1">
                          <Coins className="w-4 h-4 mr-1 inline" />
                          1 crédit
                        </Badge>
                      </div>
                      {hasEnoughCredits ? (
                        <Button
                          className="w-full bg-[#D4FF00] text-black hover:bg-[#bfe600] h-12 font-bold uppercase tracking-wide"
                          onClick={handleBooking}
                          disabled={booking}
                          data-testid="confirm-booking-btn"
                        >
                          {booking ? 'Traitement...' : 'Confirmer la réservation'}
                        </Button>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-destructive text-center">
                            Vous n'avez pas assez de crédits (besoin: 1h, disponible: {userCredits.toFixed(1)}h)
                          </p>
                          <Link to="/credits" className="block">
                            <Button
                              className="w-full bg-[#D4FF00] text-black hover:bg-[#bfe600] h-12 font-bold uppercase tracking-wide"
                              data-testid="buy-credits-btn"
                            >
                              Acheter des crédits
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
